import {
  AddressChoiceList,
  AddressForm,
  useDefaultAddressStep
} from "@/components/addresses";
import { StandaloneScreenFooter } from "@/components/layout/StandaloneScreenFooter";
import { AvatarBox } from "@/components/logo";
import { Skeleton } from "@/components/ui/skeleton";
import { firstLoginContinueTarget } from "@/lib/first-login/gate";
import { Alert, Button, Heading, Text } from "@medusajs/ui";
import { createRoute, useRouter, useSearch } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { authRoute } from "../$authRoute";

export const onboardingRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "onboarding",
  validateSearch: (
    search?: Record<string, unknown>
  ): { redirect?: string } => ({
    redirect:
      typeof search?.redirect === "string" && search.redirect
        ? search.redirect
        : undefined
  }),
  component: OnboardingPage
});

/**
 * First-login onboarding (KAN-293): the salon's delivery address.
 *
 * While the address step is on (`ADDRESS_ONBOARDING_ENABLED`), `authRoute` sends
 * a salon here when it has no default shipping address. The
 * address ends up as both the default shipping and default billing address,
 * which is what guarantees both checkouts have one: local checkout pre-selects
 * the default (`useCheckoutAddressState`) and the Shopify external-checkout
 * prefill reads `is_default_shipping`. Afterwards the salon continues to where it
 * was going.
 *
 * Two shapes, by what the salon already has:
 * - **No saved address**: the address form.
 * - **Saved addresses, none of them the default** (an account that predates the
 *   defaults, or whose default was deleted): pick one of them. Typing the same
 *   address again would just leave a duplicate behind. "Add a new address" is
 *   still there for the salon whose delivery address really is new.
 */
function OnboardingPage() {
  const { redirect } = useSearch({ from: onboardingRoute.id });
  const router = useRouter();

  // Set once a save has gone through, until the navigation away completes.
  const [leaving, setLeaving] = useState(false);

  // `router.navigate({ href })`, not `navigate({ to })`: the original target may
  // carry a query string (the cart's `?tab=`), which `to` does not parse. The
  // target has been resolved to an in-app path, so `navigate` stays in the SPA.
  const continueToTarget = useCallback(async () => {
    setLeaving(true);
    try {
      await router.navigate({
        href: firstLoginContinueTarget(redirect),
        replace: true
      });
    } catch (error) {
      // The address is saved but the router could not leave: lift the spinner so
      // the page is usable again rather than stuck on it, and let the step
      // report the error.
      setLeaving(false);
      throw error;
    }
  }, [redirect, router]);

  const {
    isLoading,
    customer,
    addresses,
    hasAddresses,
    showForm,
    defaultValues,
    chosenId,
    setPickedId,
    serverError,
    isCreating,
    isUpdating,
    handleCreate,
    handleChoose,
    startAdding,
    stopAdding
  } = useDefaultAddressStep({
    boundary: "onboarding",
    onSaved: continueToTarget
  });

  return (
    <div className="bg-ui-bg-subtle flex min-h-dvh w-dvw items-center justify-center">
      {/* `max-w-lg`: the width of `AddressDialog`, where the same form lives in
          the account. The auth pages are 280-320px because their forms are one
          column; this one has paired fields (name, city/postcode, state/country)
          that do not fit that. */}
      <div className="m-4 flex w-full max-w-lg flex-col items-center">
        <AvatarBox checked={false} />
        <div className="mb-4 flex flex-col items-center text-center">
          <Heading>
            {showForm && hasAddresses
              ? "Add a new address"
              : "Where should we deliver?"}
          </Heading>
          <Text size="small" className="text-ui-fg-subtle">
            {hasAddresses && !showForm
              ? "Choose the address we should use for delivery and billing at checkout. You can change it any time in your account."
              : "Add your salon's delivery address. We'll use it at checkout, and you can change it any time in your account."}
          </Text>
        </div>
        <div className="bg-ui-bg-base border-ui-border-base w-full rounded-lg border p-4 sm:p-6">
          {isLoading ? (
            <OnboardingSkeleton />
          ) : showForm ? (
            <AddressForm
              // The form reads its defaults once, so it mounts only after the
              // profile has arrived.
              defaultValues={defaultValues}
              showAddressName={hasAddresses}
              onSubmit={handleCreate}
              // Only a salon with something to go back to gets a way back; for
              // everyone else the gate would just send them here again.
              onCancel={hasAddresses ? stopAdding : undefined}
              isSubmitting={isCreating || leaving}
              serverError={serverError}
              submitLabel="Save and continue"
            />
          ) : (
            <div className="flex flex-col gap-y-4">
              <AddressChoiceList
                addresses={addresses}
                value={chosenId ?? ""}
                onValueChange={setPickedId}
                disabled={isUpdating || leaving}
              />
              {serverError && (
                <Alert
                  className="bg-ui-bg-base items-center p-2"
                  variant="error"
                >
                  {serverError}
                </Alert>
              )}
              <div className="flex flex-col gap-y-2">
                <Button
                  className="w-full"
                  onClick={handleChoose}
                  isLoading={isUpdating || leaving}
                  disabled={!chosenId}
                >
                  Use this address
                </Button>
                <Button
                  className="w-full"
                  variant="secondary"
                  onClick={startAdding}
                  disabled={isUpdating || leaving}
                >
                  Add a new address
                </Button>
              </div>
            </div>
          )}
        </div>
        <StandaloneScreenFooter profile={customer} />
      </div>
    </div>
  );
}

/** Stands in for the form while the profile and address list load. */
function OnboardingSkeleton() {
  return (
    <div
      className="flex flex-col gap-y-4"
      role="status"
      aria-label="Loading your details"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Skeleton className="h-10 w-full rounded-md" />
        <Skeleton className="h-10 w-full rounded-md" />
      </div>
      <Skeleton className="h-10 w-full rounded-md" />
      <Skeleton className="h-10 w-full rounded-md" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Skeleton className="h-10 w-full rounded-md" />
        <Skeleton className="h-10 w-full rounded-md" />
      </div>
      <Skeleton className="h-10 w-full rounded-md" />
    </div>
  );
}
